import { useCallback, useEffect, useId, useState, type FormEvent } from "react";
import { AnimatePresence, motion } from "motion/react";
import { ANSWER_CHARS, type Application, type Job, type Question } from "../../shared/types.ts";
import { Button, Count, Field, Input, Monogram, Select, cn, softSpring } from "../ui/index.ts";
import { answer, errorText, getApplications, isJob, retry, webUrl } from "../lib/api.ts";
import { changed, useApp } from "../lib/ctx.ts";

type Blocked = Application & { job: Job };

/** Whether an application stopped on something only the user can give. */
export const waitsOnYou = (a: Application): a is Blocked => a.status === "failed" && !!a.needs && isJob(a.job);

/** A form's label without the required star it carries, with the education block's bare date parts named as the school's. */
const asked = (label: string) =>
  label
    .replace(/\s*\*+\s*$/, "")
    .replace(/^(start|end) date (month|year)$/i, (_, end: string, part: string) => `School ${end.toLowerCase()} ${part.toLowerCase()}`);

/** What a question's box starts with: the saved answer, or for a choice the option it names, else nothing picked. */
const startOf = (q: Question) => (q.options ? (q.options.find((o) => o.toLowerCase() === q.answer.trim().toLowerCase()) ?? "") : q.answer);

/**
 * Failed applications that wait on the user, for screens that don't load the rest. Reloads whenever the engine reports
 * something. `loaded` turns true once the first load has answered, either way.
 */
export function useNeedsYou() {
  const [rows, setRows] = useState<Application[]>([]);
  const [loaded, setLoaded] = useState(false);
  const load = useCallback(() => {
    getApplications("failed").then(
      (r) => {
        setRows(r);
        setLoaded(true);
      },
      () => {},
    );
  }, []);
  useEffect(() => {
    load();
    addEventListener("engine-event", load);
    return () => removeEventListener("engine-event", load);
  }, [load]);
  const sent = useCallback((id: string) => setRows((rs) => rs.filter((r) => r.job.id !== id)), []);
  return { rows, sent, loaded };
}

/**
 * Applications that stopped on something only the user can give: required questions to answer, or a page to finish on
 * the company site, in one card. `heading` off is for a container that titles it already. `first`, a job id, puts that
 * application at the top, as when an email links to it.
 */
export function NeedsYou({
  rows,
  onSent,
  limit,
  heading = true,
  first,
  className,
}: {
  rows: Application[];
  onSent: (id: string) => void;
  limit?: number;
  heading?: boolean;
  first?: string | null;
  className?: string;
}) {
  // The one asked for, then the ones an answer here sends.
  const waiting = rows
    .filter(waitsOnYou)
    .sort((a, b) => Number(b.job.id === first) - Number(a.job.id === first) || Number(b.needs === "answers") - Number(a.needs === "answers"));
  const shown = limit ? waiting.slice(0, limit) : waiting;
  const app = useApp();
  const id = useId();

  if (!waiting.length) return null;
  const items = (
    <AnimatePresence initial={false}>
      {shown.map((r) => (
        <Item key={r.job.id} r={r} onSent={onSent} />
      ))}
    </AnimatePresence>
  );
  return (
    <section aria-labelledby={heading ? id : undefined} aria-label={heading ? undefined : "Needs you"} className={cn("flex flex-col gap-3", className)}>
      {heading && (
        <div className="flex h-7 items-center justify-between gap-3">
          <h2 id={id} className="flex items-center gap-2 text-[17px] font-bold tracking-[-0.015em] text-ink">
            Needs you
            <Count value={waiting.length} tone="brand" />
          </h2>
          {limit && waiting.length > limit && (
            <button type="button" onClick={app.showNeeds} className="text-[13px] font-semibold text-primary-ink underline-offset-2 hover:underline">
              See all
            </button>
          )}
        </div>
      )}
      <div className="overflow-hidden rounded-card bg-surface shadow-card">{items}</div>
    </section>
  );
}

function Item({ r, onSent }: { r: Blocked; onSent: (id: string) => void }) {
  const app = useApp();
  const job = r.job;
  const questions = r.questions ?? [];
  const [values, setValues] = useState(() => questions.map(startOf));
  // A reload can bring saved answers this card didn't start with; they take the place of what it showed.
  const key = JSON.stringify(questions);
  const [shown, setShown] = useState(key);
  if (shown !== key) {
    setShown(key);
    setValues(questions.map(startOf));
  }
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const ready = values.length === questions.length && values.every((v) => v.trim());
  const covered = questions.every((q) => startOf(q).trim());
  const site = webUrl(job.applyUrl) ?? webUrl(job.jobUrl);

  const sent = () => {
    app.toast(`Sending to ${job.company} again.`, "ok");
    app.refreshStats();
    onSent(job.id);
    changed();
  };
  const failed = (err: unknown) => {
    setBusy(false);
    app.toast(errorText(err), "bad");
  };

  const again = () => {
    if (busy) return;
    setBusy(true);
    retry(job.id).then(sent, failed);
  };

  const send = (e: FormEvent) => {
    e.preventDefault();
    if (!ready || busy) return;
    setBusy(true);
    answer(
      job.id,
      questions.map((q, i) => ({ label: q.label, answer: values[i]!.trim() })),
    ).then(
      (res) => {
        app.setProfile(res.profile);
        sent();
      },
      failed,
    );
  };

  return (
    <motion.div
      layout
      exit={{ opacity: 0, height: 0, transition: { duration: 0.2 } }}
      transition={softSpring}
      className="overflow-hidden border-b border-line last:border-b-0"
    >
      <div className="flex items-center gap-3 px-4 pt-4">
        <Monogram name={job.company} url={job.jobUrl} size="sm" />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[13.5px] font-semibold text-ink">{job.company}</span>
          <span className="block truncate text-[12.5px] text-muted">{job.title}</span>
        </span>
      </div>
      {r.needs === "answers" && covered && !editing ? (
        <div className="flex flex-col gap-2 p-4">
          <div className="flex items-center justify-between gap-3">
            <p className="min-w-0 text-[13px] text-ink-soft">{questions.length ? "Your saved answers cover this." : "Nothing left to answer."} Ready to send again.</p>
            <Button size="sm" className="shrink-0" loading={busy} onClick={again}>
              Send again
            </Button>
          </div>
          {questions.length > 0 && (
            <button type="button" onClick={() => setEditing(true)} className="self-start text-xs font-semibold text-primary-ink underline-offset-2 hover:underline">
              Change answers
            </button>
          )}
        </div>
      ) : r.needs === "answers" ? (
        <form onSubmit={send} className="flex flex-col gap-3 p-4">
          {questions.map((q, i) => {
            const set = (v: string) => setValues((vs) => vs.map((x, j) => (j === i ? v : x)));
            return (
              <Field key={q.label} label={asked(q.label)}>
                {q.options ? (
                  <Select value={values[i] ?? ""} onChange={(e) => set(e.target.value)}>
                    <option value="">Choose one</option>
                    {q.options.map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </Select>
                ) : (
                  <Input value={values[i] ?? ""} maxLength={ANSWER_CHARS} onChange={(e) => set(e.target.value)} />
                )}
              </Field>
            );
          })}
          <Button type="submit" size="sm" loading={busy} disabled={!ready}>
            Answer and send
          </Button>
          <p className="text-xs text-muted">Saved for all forms.</p>
        </form>
      ) : (
        <div className="flex items-center justify-between gap-3 p-4">
          <p className="min-w-0 text-[13px] text-ink-soft">{r.reasonLabel}.</p>
          {site && (
            <a
              href={site}
              target="_blank"
              rel="noreferrer"
              className="shrink-0 whitespace-nowrap text-[13px] font-semibold text-primary-ink underline-offset-2 hover:underline"
            >
              Finish on site
            </a>
          )}
        </div>
      )}
    </motion.div>
  );
}
